import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { getCurrencyList, getCurrencySymbol } from '../lib/utils';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  Plug,
  BarChart3,
  Loader2,
  Mail,
  User,
  ExternalLink,
  DollarSign,
} from 'lucide-react';

const ALL_PLATFORMS = [
  { value: 'Meta', label: 'Meta (Facebook/Instagram)', icon: '📘' },
  { value: 'DV360', label: 'Google DV360', icon: '🎯' },
  { value: 'TikTok', label: 'TikTok Ads', icon: '🎵' },
  { value: 'LinkedIn', label: 'LinkedIn Ads', icon: '💼' },
  { value: 'Twitter', label: 'Twitter/X Ads', icon: '🐦' },
  { value: 'Mondiads', label: 'Mondiads', icon: '📊' },
  { value: 'Propeller', label: 'PropellerAds', icon: '🚀' },
  { value: 'Clickadu', label: 'Clickadu', icon: '🖱️' },
];

const CURRENCY_LIST = getCurrencyList();

export const Advertisers = () => {
  const navigate = useNavigate();
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAdvertiser, setNewAdvertiser] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    logo_url: '',
    notes: '',
    allowed_platforms: null, // null means all platforms
    currency: 'USD',
    user_password: '',  // Always required
  });
  const [selectAllPlatforms, setSelectAllPlatforms] = useState(true);

  const fetchAdvertisers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/advertisers');
      setAdvertisers(response.data);
    } catch (error) {
      toast.error('Failed to fetch advertisers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvertisers();
  }, []);

  const handleCreate = async () => {
    if (!newAdvertiser.name.trim()) {
      toast.error('Advertiser name is required');
      return;
    }
    
    if (!newAdvertiser.contact_email.trim()) {
      toast.error('Contact email is required');
      return;
    }
    
    if (!newAdvertiser.user_password || newAdvertiser.user_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreating(true);
    try {
      await api.post('/advertisers', {
        ...newAdvertiser,
      });
      toast.success('Advertiser created with user account');
      setCreateDialogOpen(false);
      setNewAdvertiser({ name: '', contact_name: '', contact_email: '', logo_url: '', notes: '', allowed_platforms: null, currency: 'USD', user_password: '' });
      setSelectAllPlatforms(true);
      fetchAdvertisers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create advertiser');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (advertiser) => {
    if (!window.confirm(`Are you sure you want to delete "${advertiser.name}"? This will delete all associated data.`)) {
      return;
    }

    try {
      await api.delete(`/advertisers/${advertiser.id}`);
      toast.success('Advertiser deleted');
      fetchAdvertisers();
    } catch (error) {
      toast.error('Failed to delete advertiser');
    }
  };

  const filteredAdvertisers = advertisers.filter(a =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.contact_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Advertisers</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage advertisers and their platform integrations
            </p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-advertiser-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Advertiser
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Advertiser</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Acme Agency"
                    value={newAdvertiser.name}
                    onChange={(e) => setNewAdvertiser({ ...newAdvertiser, name: e.target.value })}
                    data-testid="advertiser-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      placeholder="John Doe"
                      value={newAdvertiser.contact_name}
                      onChange={(e) => setNewAdvertiser({ ...newAdvertiser, contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      placeholder="john@example.com"
                      value={newAdvertiser.contact_email}
                      onChange={(e) => setNewAdvertiser({ ...newAdvertiser, contact_email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <Input
                    id="logo_url"
                    placeholder="https://example.com/logo.png"
                    value={newAdvertiser.logo_url}
                    onChange={(e) => setNewAdvertiser({ ...newAdvertiser, logo_url: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Additional notes..."
                    value={newAdvertiser.notes}
                    onChange={(e) => setNewAdvertiser({ ...newAdvertiser, notes: e.target.value })}
                  />
                </div>
                
                {/* Currency Selection */}
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <p className="text-xs text-slate-500">Select the currency for this advertiser's spend data</p>
                  <Select 
                    value={newAdvertiser.currency} 
                    onValueChange={(value) => setNewAdvertiser({ ...newAdvertiser, currency: value })}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {CURRENCY_LIST.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          <span className="flex items-center gap-2">
                            <span className="font-mono text-sm">{currency.symbol}</span>
                            <span>{currency.code}</span>
                            <span className="text-slate-500">- {currency.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Platform Access Control */}
                <div className="space-y-3">
                  <Label>Allowed Platforms</Label>
                  <p className="text-xs text-slate-500">Select which platforms this advertiser can use</p>
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      id="select-all-platforms"
                      checked={selectAllPlatforms}
                      onCheckedChange={(checked) => {
                        setSelectAllPlatforms(checked);
                        if (checked) {
                          setNewAdvertiser({ ...newAdvertiser, allowed_platforms: null });
                        } else {
                          setNewAdvertiser({ ...newAdvertiser, allowed_platforms: [] });
                        }
                      }}
                      data-testid="select-all-platforms"
                    />
                    <Label htmlFor="select-all-platforms" className="text-sm font-medium cursor-pointer">
                      All Platforms
                    </Label>
                  </div>
                  {!selectAllPlatforms && (
                    <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      {ALL_PLATFORMS.map((platform) => (
                        <div key={platform.value} className="flex items-center gap-2">
                          <Checkbox
                            id={`platform-${platform.value}`}
                            checked={newAdvertiser.allowed_platforms?.includes(platform.value) || false}
                            onCheckedChange={(checked) => {
                              const current = newAdvertiser.allowed_platforms || [];
                              if (checked) {
                                setNewAdvertiser({
                                  ...newAdvertiser,
                                  allowed_platforms: [...current, platform.value]
                                });
                              } else {
                                setNewAdvertiser({
                                  ...newAdvertiser,
                                  allowed_platforms: current.filter(p => p !== platform.value)
                                });
                              }
                            }}
                            data-testid={`platform-checkbox-${platform.value}`}
                          />
                          <Label 
                            htmlFor={`platform-${platform.value}`} 
                            className="text-sm cursor-pointer flex items-center gap-1"
                          >
                            <span>{platform.icon}</span>
                            <span>{platform.value}</span>
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* User Account Password - Always Required */}
                <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-600" />
                    <Label className="text-sm font-medium text-blue-900">User Account</Label>
                  </div>
                  <p className="text-xs text-blue-700">
                    A user account will be automatically created with the contact email above
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="user_password">Password *</Label>
                    <Input
                      id="user_password"
                      type="password"
                      value={newAdvertiser.user_password}
                      onChange={(e) => setNewAdvertiser({ ...newAdvertiser, user_password: e.target.value })}
                      placeholder="Set password for user account (min 6 characters)"
                      data-testid="user-password-input"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={handleCreate} 
                  disabled={creating} 
                  className="w-full"
                  data-testid="submit-advertiser-btn"
                >
                  {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create Advertiser
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search advertisers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-advertisers"
          />
        </div>

        {/* Advertisers Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredAdvertisers.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">
                {searchQuery ? 'No advertisers found' : 'No advertisers yet'}
              </h3>
              <p className="text-slate-500 mb-4">
                {searchQuery ? 'Try a different search term' : 'Create your first advertiser to get started'}
              </p>
              {!searchQuery && (
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Advertiser
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAdvertisers.map((advertiser) => (
              <Card
                key={advertiser.id}
                className="border-slate-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/advertisers/${advertiser.id}`)}
                data-testid={`advertiser-card-${advertiser.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {advertiser.logo_url ? (
                        <img
                          src={advertiser.logo_url}
                          alt={advertiser.name}
                          className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {advertiser.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-900">{advertiser.name}</h3>
                        {advertiser.contact_name && (
                          <p className="text-sm text-slate-500">{advertiser.contact_name}</p>
                        )}
                      </div>
                    </div>
                    <div className={`w-2 h-2 rounded-full ${advertiser.is_active ? 'bg-green-500' : 'bg-slate-300'}`} />
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <Plug className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                      <p className="text-lg font-semibold text-slate-900">{advertiser.integration_count}</p>
                      <p className="text-xs text-slate-500">Integrations</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <BarChart3 className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                      <p className="text-lg font-semibold text-slate-900">{advertiser.campaign_count}</p>
                      <p className="text-xs text-slate-500">Campaigns</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <Users className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                      <p className="text-lg font-semibold text-slate-900">-</p>
                      <p className="text-xs text-slate-500">Users</p>
                    </div>
                  </div>

                  {/* Contact */}
                  {advertiser.contact_email && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{advertiser.contact_email}</span>
                    </div>
                  )}
                  
                  {/* Currency Badge */}
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">
                      {advertiser.currency || 'USD'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/advertisers/${advertiser.id}`);
                      }}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(advertiser);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Advertisers;
